import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getJSON, patchJSON, postJSON } from '@/lib/api'
import type { Connection, ConnectionResponse, SortColumn } from './types'

export function useConnectionsList() {
  const [search, setSearch] = useState('')
  const [types, setTypes] = useState<ReadonlySet<string>>(new Set())
  const [healths, setHealths] = useState<ReadonlySet<string>>(new Set())
  const [statuses, setStatuses] = useState<ReadonlySet<string>>(new Set())
  const [showRemoved, setShowRemoved] = useState(false)
  const [sort, setSort] = useState<SortColumn>('last_run')
  const [direction, setDirection] = useState<'asc' | 'desc'>('desc')
  const params = new URLSearchParams({ sort, direction })
  if (search) params.set('q', search)
  if (showRemoved || statuses.has('removed')) params.set('show_removed', 'true')
  addFacet(params, 'connection_type', types)
  addFacet(params, 'health', healths)
  addFacet(params, 'status', statuses)
  const rows = useQuery({
    queryKey: ['connections', params.toString()],
    queryFn: () => getJSON<ConnectionResponse>(`/api/v1/connections?${params}`),
  })
  const summary = useQuery({
    queryKey: ['connections', 'summary'],
    queryFn: () => getJSON<ConnectionResponse>('/api/v1/connections'),
  })
  const sortable = (column: SortColumn) => ({
    active: sort === column,
    direction: direction === 'asc' ? ('ascending' as const) : ('descending' as const),
    onToggle: () => {
      setDirection(nextDirection(sort, column, direction))
      setSort(column)
    },
  })
  return {
    search,
    setSearch,
    types,
    setTypes,
    healths,
    setHealths,
    statuses,
    setStatuses,
    showRemoved,
    setShowRemoved,
    rows,
    summary,
    sortable,
  }
}

function addFacet(params: URLSearchParams, key: string, values: ReadonlySet<string>) {
  values.forEach((value) => params.append(`${key}[]`, value))
}
function nextDirection(current: SortColumn, next: SortColumn, direction: 'asc' | 'desc') {
  if (current !== next) return next === 'name' ? 'asc' : 'desc'
  return direction === 'asc' ? 'desc' : 'asc'
}

export type ConnectionCommand = { row: Connection; action: string; name?: string }

export function listSummary(rows: Connection[] | undefined) {
  return {
    attention:
      rows?.filter((row) => row.health === 'needs_attention' && row.status === 'active') ?? [],
    unmatchedTotal: rows?.reduce((total, row) => total + row.unmatched_count, 0),
    emptyTitle: rows?.length === 0 ? 'No connections yet' : 'No matching connections',
  }
}
export function useConnectionAction() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ row, action, name }: ConnectionCommand) => {
      const path = `/api/v1/connections/${row.id}`
      if (action === 'rename') return patchJSON(path, { connection: { display_name: name } })
      if (action === 'remove') return patchJSON(path, { connection: { status: 'removed' } })
      return postJSON(`${path}/${action}`, {})
    },
    onSuccess: () => client.invalidateQueries({ queryKey: ['connections'] }),
  })
}
